import * as React from "react";
import { Combobox, Button, ButtonGroup, DropdownMenu, Spinner, InputGroup, Input, Tooltip } from "@/components/ui";
import {
  ChevronDown,
  ChevronUp,
  Search,
  Check,
  X,
  List,
  LayoutGrid,
  MoreHorizontal,
  Megaphone,
  Upload,
  LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { debounce } from "lodash";

import * as DTO from "../../../../interfaces";
import { useAccountSettings, URLS, useCategories, useLocalStorage, useSocialCoachSession, STORAGE_KEYS } from "@/utils";
import { PromptServices } from "@/services";

import { UploadModal } from "./UploadModal";

import { SearchFilter, WebComponents } from "@/interfaces";
import { getDownloadURL } from "firebase/storage";
import { useMemo } from "react";
import { PromptCategoryCards } from "@/components/PromptCategoryCards";
import PromptsList from "./PromptList";
import pluralize from "pluralize";
import { useHistory } from "react-router-dom";
import { PromptStudioEntryCard } from "@/containers/Coach/PromptStudio/components/PromptStudioEntryCard";
import { PromptKindFilter } from "@/components/PromptKindFilter";
import { useDynamicPromptsAccess } from "@/utils/useDynamicPromptsAccess";
import { filterByKind, PromptKind } from "./promptKind";

const EMPTY_KIND_MESSAGE: Record<Exclude<PromptKind, "ALL">, string> = {
  DYNAMIC: "No dynamic prompts yet. Publish a design in Prompt Studio to see it here.",
  STATIC: "No static prompts match.",
};

const Prompts: React.FC = () => {
  const history = useHistory();
  const { coachId } = useSocialCoachSession();
  const { hasWritePermission } = useAccountSettings();
  const writePermission = hasWritePermission(WebComponents.PROMPTS);
  const writeCampaignPermission = hasWritePermission(WebComponents.CAMPAIGNS);
  const { canView: canSeeDynamic, loading: dynamicAccessLoading } = useDynamicPromptsAccess();
  const [storedPromptKind, setPromptKind] = useLocalStorage<PromptKind>("libraryPromptKind", "ALL");

  const [displayLoadingSection, setDisplayLoadingSection] = React.useState(true);
  const [promptsBulkDic, setPromptsBulkDic] = React.useState<{ [key: string]: DTO.PromptPreview }>();

  const [refresh, setRefresh] = React.useState(false);
  const [uploadEnable, setUploadEnable] = React.useState(true);

  const [uploading, setUploading] = React.useState(false);
  const [bulkRequest, setBulkRequest] = React.useState<DTO.PromptBulkRequest>();

  React.useEffect(() => {
    if (!uploadEnable) {
      Object.values(promptsBulkDic!).map(({ uploadTask }) => {
        if (uploadTask) {
          uploadTask.cancel();
        }
      });
      setPromptsBulkDic(undefined);
      setBulkRequest(undefined);
    }
  }, [uploadEnable]);

  React.useEffect(() => {
    if (uploadEnable && bulkRequest) {
      setUploadEnable(false);
      PromptServices.createBulk(bulkRequest).finally(() => {
        setPromptsBulkDic(undefined);
        setRefresh(true);
        setBulkRequest(undefined);
      });
    } else if (!uploadEnable && bulkRequest) {
      setPromptsBulkDic(undefined);
      setRefresh(true);
      setBulkRequest(undefined);
    }
  }, [bulkRequest]);

  React.useEffect(() => {
    if (uploading && uploadEnable) {
      uploadPrompts();
      setUploading(false);
    }
  }, [promptsBulkDic]);

  const createCampaign = () => {
    localStorage.removeItem(STORAGE_KEYS.COACH_NEW_CAMPAIGN_STEP);
    localStorage.removeItem(STORAGE_KEYS.COACH_CAMPAIGN);
    history.push(URLS.coach.newCampaigns);
  };

  const addPromptHandler = () => {
    history.push(URLS.coach.newPrompt, {
      deliveryMode: "LIBRARY",
    });
  };

  const uploadPrompts = (): void => {
    if (promptsBulkDic) {
      const uploadTaskPromises = Object.values(promptsBulkDic!).map(({ uploadTask }) => {
        return uploadTask;
      });
      if (uploadTaskPromises) {
        Promise.all(uploadTaskPromises).then(values => {
          const imagesPromises = Object.values(values).map(uploadTask => {
            return getDownloadURL(uploadTask!.ref);
          });

          Promise.all(imagesPromises).then(values2 => {
            const prompts: DTO.PromptRequest[] = [];
            if (promptsBulkDic) {
              Object.values(promptsBulkDic!).map(
                async ({ promptId, categoryIds, title, message, richMessage, status, deliveryMode, post }, index) => {
                  const url = values2[index];

                  const newPost: DTO.MediaGroup = {
                    media: post?.media || [],
                    mediaGroupId: undefined,
                    type: post!.type,
                  };

                  newPost!.media[0]!.uri = url;
                  prompts.push({
                    promptId,
                    coachId,
                    title,
                    message,
                    richMessage,
                    status,
                    deliveryMode,
                    post: newPost,
                    categoryIds,
                  });
                }
              );
              const request: DTO.PromptBulkRequest = {
                coachId: coachId!,
                deliveryMode: "LIBRARY",
                promptList: prompts,
              };
              setBulkRequest(request);
            }
          });
        });
      }
    }
  };
  const [promptList, setPromptList] = React.useState<DTO.Prompt[]>([]);

  type ViewType = { value: "LIST" | "CARD"; label: string; icon: LucideIcon };
  const viewTypes: ViewType[] = [
    { value: "LIST", label: "List view", icon: List },
    { value: "CARD", label: "Card view", icon: LayoutGrid },
  ];
  const [selectedViewTypeValue, setSelectedViewTypeValue] = useLocalStorage<ViewType["value"]>(
    "libraryViewType",
    viewTypes[0].value
  );
  const [uploadOpen, setUploadOpen] = React.useState(false);

  const [loading, setLoading] = React.useState<boolean>(true);

  const [searchTitle, setSearchTitle] = React.useState<string | null>(null);
  const [selectedCategories, setSelectedCategories] = React.useState<DTO.Category[]>([]);

  const { categories } = useCategories("LIBRARY");

  // Responses can land out of order once access resolves; only the newest one is applied.
  const latestSearch = React.useRef(0);

  const search = (
    searchContent: string | null = searchTitle,
    categories: DTO.Category[] | null = selectedCategories
  ) => {
    // Wait for access so the first request already knows whether to ask for dynamic prompts.
    if (dynamicAccessLoading) return;
    const searchId = ++latestSearch.current;
    setLoading(true);

    const categoryIds: string[] = (categories?.map(category => category.categoryId)?.filter(Boolean) as string[]) || [];
    const filterBody: SearchFilter = {
      title: searchContent,
      status: categoryIds,
      deliveryModes: canSeeDynamic ? ["LIBRARY", "WELCOME_PROMPT", "DYNAMIC"] : undefined,
    };

    PromptServices.library(coachId!, "createdDate,DESC", filterBody)
      .then(response => {
        if (searchId === latestSearch.current) setPromptList(response.content);
      })
      .catch(console.error)
      .finally(() => {
        if (searchId === latestSearch.current) setLoading(false);
      });
  };

  React.useEffect(search, []);
  React.useEffect(search, [selectedCategories, searchTitle, canSeeDynamic, dynamicAccessLoading]);
  React.useEffect(() => {
    if (refresh) {
      search();
    }
  }, [refresh]);

  const delayedQuery = React.useRef(debounce(setSearchTitle, 500)).current;
  const onChange = (e: any) => {
    delayedQuery(e.target.value);
  };

  // Without dynamic prompts the filter isn't shown, and the stored choice waits for when it is.
  const promptKind: PromptKind = canSeeDynamic ? storedPromptKind : "ALL";
  const visiblePrompts = useMemo(() => filterByKind(promptList, promptKind), [promptList, promptKind]);
  const emptyMessage =
    visiblePrompts.length === 0 && promptList.length > 0 && promptKind !== "ALL"
      ? EMPTY_KIND_MESSAGE[promptKind]
      : undefined;

  const content = useMemo(() => {
    if (loading) {
      return <Spinner className="size-12 self-center mt-25" />;
    } else if (selectedViewTypeValue === "LIST") {
      return <PromptsList categories={categories} promptList={visiblePrompts} emptyMessage={emptyMessage} />;
    } else {
      return <PromptCategoryCards categories={categories} promptList={visiblePrompts} />;
    }
  }, [selectedViewTypeValue, categories, visiblePrompts, emptyMessage, loading]);

  return (
    <div className="flex-auto flex flex-col gap-6">
      {canSeeDynamic && <PromptStudioEntryCard />}
      <div className="flex items-center flex-wrap justify-between gap-4">
        <div className="flex gap-4 items-center flex-wrap flex-auto">
          <InputGroup
            className="max-w-80 w-auto min-w-50"
            control={controlProps => <Input {...controlProps} placeholder="Search..." onChange={onChange} />}
            append={<Search />}
          />
          {categories && categories?.length > 0 && (
            <Combobox
              items={categories}
              value={selectedCategories}
              itemToStringValue={category => category.name}
              itemToStringLabel={category => category.name}
              onValueChange={value => setSelectedCategories(value)}
              placeholder="Filter by Categories"
              emptyText="No Categories found."
              showClear
              searchable
              className="max-w-80 min-w-50"
              maxChips="auto"
              multiple
            />
          )}
          {canSeeDynamic && <PromptKindFilter value={promptKind} onChange={setPromptKind} audience="coach" />}
          <ButtonGroup aria-label="View" className="rounded-lg border border-slate-200 bg-white p-0.5">
            {viewTypes.map(({ value, label, icon: Icon }) => {
              const selected = selectedViewTypeValue === value;
              return (
                <Tooltip key={value}>
                  <Tooltip.Trigger
                    render={
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label={label}
                        aria-pressed={selected}
                        className={cn(
                          "rounded-md!",
                          selected ? "bg-slate-100 text-slate-900 shadow-xs" : "text-slate-500 hover:text-slate-700"
                        )}
                        onClick={() => setSelectedViewTypeValue(value)}
                      />
                    }
                  >
                    <Icon aria-hidden="true" />
                  </Tooltip.Trigger>
                  <Tooltip.Content>{label}</Tooltip.Content>
                </Tooltip>
              );
            })}
          </ButtonGroup>
        </div>
        <div className="flex gap-2 items-center">
          {(writeCampaignPermission || writePermission) && (
            <DropdownMenu>
              <DropdownMenu.Trigger
                render={
                  <Button variant="outline" size="icon" aria-label="More actions">
                    <MoreHorizontal aria-hidden="true" />
                  </Button>
                }
              />
              <DropdownMenu.Content align="end">
                {writeCampaignPermission && (
                  <DropdownMenu.Item onClick={createCampaign}>
                    <Megaphone aria-hidden="true" />
                    Create a Campaign
                  </DropdownMenu.Item>
                )}
                {writePermission && (
                  <DropdownMenu.Item onClick={() => setUploadOpen(true)}>
                    <Upload aria-hidden="true" />
                    Upload Files
                  </DropdownMenu.Item>
                )}
              </DropdownMenu.Content>
            </DropdownMenu>
          )}
          {writePermission && (
            <UploadModal
              open={uploadOpen}
              onOpenChange={setUploadOpen}
              onUploaded={submitPrompts => {
                setUploadEnable(true);
                setUploading(true);
                setPromptsBulkDic(submitPrompts);
              }}
              modalOpened={() => setRefresh(false)}
              submittingFiles={submitPrompts => {
                if (uploadEnable) {
                  setPromptsBulkDic(submitPrompts);
                }
              }}
            />
          )}
          {writePermission && <Button onClick={addPromptHandler} children="New Prompt" />}
        </div>
      </div>

      {content}

      {promptsBulkDic && (
        <div className="bg-white max-h-115 overflow-x-auto fixed bottom-0 right-2 m-0 p-0 rounded-lg w-75 shadow-sm flex flex-col">
          <div className="bg-accent py-3">
            <Button
              variant="ghost"
              size="lg"
              className="font-semibold w-full text-accent-foreground *:bg-transparent"
              onClick={() => setDisplayLoadingSection(!displayLoadingSection)}
            >
              <div className="text-center basis-full">{`Uploading ${Object.keys(promptsBulkDic).length} ${pluralize(
                "File",
                Object.keys(promptsBulkDic).length
              )}`}</div>
              {displayLoadingSection ? <ChevronDown className="size-6" /> : <ChevronUp className="size-6" />}
            </Button>
          </div>
          <Button variant="link" className="self-end" onClick={() => setUploadEnable(false)} children="Cancel" />

          {displayLoadingSection &&
            Object.values(promptsBulkDic).map((prompt, index) => {
              let icon = <Spinner className="size-6 min-size-6 min-w-6 min-h-6" />;
              if (prompt.loadStatus === "SUBMITTED") {
                icon = (
                  <Check
                    className="size-6 min-size-6 min-w-6 min-h-6 bg-primary text-white rounded-full p-1"
                    strokeWidth={3}
                  />
                );
              } else if (prompt.loadStatus === "FAILED") {
                icon = (
                  <X
                    className="size-6 min-size-6 min-w-6 min-h-6 bg-destructive text-white rounded-full p-1"
                    strokeWidth={3}
                  />
                );
              }
              return (
                <div key={index} className="py-4 px-5 h-15 flex flex-row items-center justify-between gap-4">
                  <div className="overflow-hidden text-ellipsis whitespace-nowrap">{prompt.title}</div>
                  {icon}
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
};

export default Prompts;
